import React, { useEffect, useState } from "react";
import api from "../../config/axios";
import Header from "../common/Header";
import { useNavigate } from "react-router-dom";
import "./TableManagement.css";

interface Table {
  _id: string;
  name: string;
  seats: number;
  status: string;
  qrCode?: string;
}

interface Order {
  _id: string;
  items: { name: string; quantity: number; price: number }[];
  total: number;
  status: string;
  createdAt: string;
}

const fakeUser = { username: 'admin', role: 'admin' };

const TableManagement: React.FC = () => {
  const [tables, setTables] = useState<Table[]>([]);
  const [name, setName] = useState("");
  const [seats, setSeats] = useState(4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSeats, setEditSeats] = useState(4);
  const [showOrdersTableId, setShowOrdersTableId] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const startEdit = (table: Table) => {
    setEditId(table._id);
    setEditName(table.name);
    setEditSeats(table.seats);
    setError(""); setSuccess("");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditSeats(4);
  };
  
  const handleEditSave = async (id: string) => {
    setError(""); setSuccess("");
    if (!editName.trim() || editSeats < 1) {
      setError("Tên bàn và số ghế hợp lệ là bắt buộc");
      return;
    }
    try {
      await api.put(`/api/tables/${id}`, { name: editName, seats: editSeats });
      setSuccess("Đã cập nhật bàn!");
      setEditId(null);
      fetchTables();
    } catch (err: any) {
      setError(err.response?.data?.message || "Lỗi khi cập nhật bàn");
    }
  };

  const fetchTables = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/tables");
      setTables(res.data);
    } catch {
      setError("Không thể tải danh sách bàn");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersForTable = async (tableId: string) => {
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const res = await api.get(`/api/orders?tableId=${tableId}`);
      setOrders(res.data.data);
    } catch (err: any) {
      setOrdersError("Không thể tải đơn hàng của bàn này");
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleShowOrders = (tableId: string) => {
    setShowOrdersTableId(tableId);
    fetchOrdersForTable(tableId);
  };

  const handleCloseOrders = () => {
    setShowOrdersTableId(null);
    setOrders([]);
  };

  const handleCreateOrder = (tableId: string) => {
    navigate(`/orders/new?tableId=${tableId}`);
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!name.trim() || seats < 1) {
      setError("Tên bàn và số ghế hợp lệ là bắt buộc");
      return;
    }
    try {
      await api.post("/api/tables", { name, seats });
      setSuccess("Đã thêm bàn mới!");
      setName(""); setSeats(4);
      fetchTables();
    } catch (err: any) {
      setError(err.response?.data?.message || "Lỗi khi thêm bàn");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Bạn chắc chắn muốn xóa bàn này?")) return;
    setError(""); setSuccess("");
    try {
      await api.delete(`/api/tables/${id}`);
      setSuccess("Đã xóa bàn!");
      fetchTables();
    } catch (err: any) {
      setError(err.response?.data?.message || "Lỗi khi xóa bàn");
    }
  };

  const handleRegenerateAllQR = async () => {
    if (!window.confirm("Bạn chắc chắn muốn tạo lại QR code cho tất cả các bàn?")) return;
    setError(""); setSuccess("");
    try {
      await api.post(`/api/tables/regenerate-qr`, {});
      setSuccess("Đã tạo lại QR code cho tất cả các bàn!");
      fetchTables();
    } catch (err: any) {
      setError(err.response?.data?.message || "Lỗi khi tạo lại QR code");
    }
  };

  const handleRegenerateQR = async (tableId: string) => {
    setError(""); setSuccess("");
    try {
      await api.post(`/api/tables/${tableId}/regenerate-qr`, {});
      setSuccess("Đã tạo lại QR code!");
      fetchTables();
    } catch (err: any) {
      setError(err.response?.data?.message || "Lỗi khi tạo lại QR code");
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Trống';
      case 'reserved': return 'Đã đặt';
      case 'in-use': return 'Đang sử dụng';
      case 'completed': return 'Hoàn thành';
      case 'expired': return 'Quá hạn';
      case 'cancelled': return 'Đã hủy';
      default: return status;
    }
  };

  const getOrderStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ thanh toán';
      case 'paid': return 'Đã thanh toán';
      case 'cancelled': return 'Đã hủy';
      case 'merged': return 'Đã gộp';
      default: return status;
    }
  };

  return (
    <>
      <Header user={fakeUser} onLogout={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/'; }} />
      <div className="table-management-container">
        <div className="table-management-header">
          <button className="table-management-back-btn" onClick={() => navigate(-1)} title="Quay lại">←</button>
          <h2 className="table-management-title">QUẢN LÝ BÀN</h2>
        </div>
        <form className="table-management-form" onSubmit={handleAdd}>
          <div className="form-group">
            <label htmlFor="tableName">Tên bàn:</label>
            <input 
              id="tableName"
              type="text" 
              placeholder="Nhập tên bàn" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              required 
            />
          </div>
          <div className="form-group">
            <label htmlFor="tableSeats">Số ghế:</label>
            <input 
              id="tableSeats"
              type="number" 
              placeholder="Nhập số ghế" 
              value={seats} 
              min={1} 
              max={20} 
              onChange={e => setSeats(Number(e.target.value))} 
              required 
            />
          </div>
          <button type="submit">Thêm bàn</button>
        </form>
        <div className="qr-regenerate-section">
          <button 
            onClick={handleRegenerateAllQR}
            className="qr-regenerate-btn"
          >
            Tạo lại QR code cho tất cả bàn
          </button>
        </div>
        {error && <div className="table-management-message error">{error}</div>}
        {success && <div className="table-management-message success">{success}</div>}
        {loading ? <div>Đang tải...</div> : (
          <table className="table-management-table">
            <thead>
              <tr>
                <th>Tên bàn</th>
                <th>Số ghế</th>
                <th>Trạng thái</th>
                <th>QR code</th>
                <th>Xóa</th>
              </tr>
            </thead>
            <tbody>
              {tables.map(table => (
                <tr key={table._id}>
                  {editId === table._id ? (
                    <>
                      <td><input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: 80 }} /></td>
                      <td><input type="number" value={editSeats} min={1} max={20} onChange={e => setEditSeats(Number(e.target.value))} style={{ width: 60 }} /></td>
                      <td>{getStatusText(table.status)}</td>
                      <td></td>
                      <td>
                        <button className="table-management-delete-btn" style={{ background: '#1976d2', marginRight: 6 }} onClick={() => handleEditSave(table._id)}>Lưu</button>
                        <button className="table-management-delete-btn" style={{ background: '#bdbdbd', color: '#222' }} onClick={cancelEdit}>Hủy</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{table.name}</td>
                      <td>{table.seats}</td>
                      <td onClick={() => handleShowOrders(table._id)} style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600 }}>{getStatusText(table.status)}</td>
                      <td>
                        {table.qrCode && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img src={table.qrCode} alt={`QR bàn ${table.name}`} style={{ width: 64, height: 64, marginBottom: 4, border: '1px solid #ccc', background: '#fff' }} />
                            <a href={table.qrCode} download={`table-${table.name}-qr.png`} style={{ fontSize: 12 }}>
                              <button type="button" style={{ padding: '2px 8px', fontSize: 12 }}>Tải QR</button>
                            </a>
                            <button 
                              onClick={() => handleRegenerateQR(table._id)}
                              style={{ 
                                padding: '2px 8px', 
                                fontSize: 12, 
                                background: '#ff9800', 
                                color: '#fff', 
                                border: 'none', 
                                borderRadius: 3, 
                                cursor: 'pointer',
                                marginTop: 2
                              }}
                            >
                              Tạo lại
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <button className="table-management-delete-btn" style={{ background: '#1976d2', marginRight: 6 }} onClick={() => startEdit(table)}>Sửa</button>
                        <button className="table-management-delete-btn" onClick={() => handleDelete(table._id)}>Xóa</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {showOrdersTableId && (
          <div className="table-management-modal-bg">
            <div className="table-management-modal">
              <h3>Đơn hàng của bàn</h3>
              {ordersLoading ? <div>Đang tải đơn hàng...</div> : ordersError ? <div style={{ color: 'red' }}>{ordersError}</div> : (
                <>
                  {orders.length === 0 ? <div>Chưa có đơn hàng nào cho bàn này.</div> : (
                    <table className="table-management-orders-table">
                      <thead>
                        <tr>
                          <th>Mã đơn</th>
                          <th>Thời gian</th>
                          <th>Tổng tiền</th>
                          <th>Trạng thái</th>
                          <th>Số món</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <tr key={order._id} onClick={() => setSelectedOrder(order)} style={{ cursor: 'pointer' }}>
                            <td>{order._id.slice(-6).toUpperCase()}</td>
                            <td>{new Date(order.createdAt).toLocaleString()}</td>
                            <td>{order.total.toLocaleString()} đ</td>
                            <td>{getOrderStatusText(order.status)}</td>
                            <td>{order.items.length}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
              <button onClick={() => handleCreateOrder(showOrdersTableId)} style={{ marginTop: 16, background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>Tạo đơn mới cho bàn này</button>
              <button onClick={handleCloseOrders} style={{ marginLeft: 12, background: '#aaa', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>Đóng</button>
            </div>
          </div>
        )}
        {selectedOrder && (
          <div className="table-management-modal-bg">
            <div className="table-management-modal">
              <h3>Chi tiết đơn hàng #{selectedOrder._id.slice(-6).toUpperCase()}</h3>
              <div><b>Thời gian:</b> {new Date(selectedOrder.createdAt).toLocaleString()}</div>
              <div><b>Trạng thái:</b> {getOrderStatusText(selectedOrder.status)}</div>
              <div><b>Tổng tiền:</b> {selectedOrder.total.toLocaleString()} đ</div>
              <table className="table-management-orders-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Tên món</th>
                    <th>Số lượng</th>
                    <th>Đơn giá</th>
                    <th>Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.price.toLocaleString()} đ</td>
                      <td>{(item.price * item.quantity).toLocaleString()} đ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => setSelectedOrder(null)} style={{ marginTop: 16, background: '#aaa', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>Đóng</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TableManagement; 